/**
 * milestoneFlow.js — the firm-wide index of started-ahead links and post records
 * (docs/COMPRESSED_CHAIN.md, database/381). Same lazy contract as outline/crew.js:
 *
 *   ctx.milestoneFlow  undefined  not started (the first read kicks it off)
 *                      null       in flight
 *                      object     { aheadByMs: { msId: [row] }, postByMs: { msId: row } }
 *
 * Both tables are small (a handful of rows per project), so one firm-wide read serves every screen.
 * Reloads on `milestone-flow-changed`, fired by services/milestoneFlow.js after each write.
 */
import { ctx } from './ctx.js'
import { listMilestoneFlow } from '../services/milestoneFlow.js'

let inflight = null

function build(d) {
  const aheadByMs = {}, postByMs = {}
  for (const r of d.startAhead || []) (aheadByMs[r.milestone_airtable_id] = aheadByMs[r.milestone_airtable_id] || []).push(r)
  for (const r of d.postRecords || []) postByMs[r.milestone_airtable_id] = r
  return { aheadByMs, postByMs }
}

export function ensureMilestoneFlow() {
  if (ctx.milestoneFlow) return Promise.resolve(ctx.milestoneFlow)
  if (inflight) return inflight
  ctx.milestoneFlow = null
  inflight = listMilestoneFlow()
    // An unreadable index starts nothing ahead: blocks stay as they were.
    .then(d => { ctx.milestoneFlow = build(d) }, () => { ctx.milestoneFlow = build({}) })
    .then(() => { inflight = null; if (typeof window !== 'undefined') window.dispatchEvent(new Event('outline-render')); return ctx.milestoneFlow })
  return inflight
}

function index() {
  if (ctx.milestoneFlow === undefined) ensureMilestoneFlow()
  return ctx.milestoneFlow || null
}

/** The started-ahead rows on a milestone (each names the predecessor it went ahead of). */
export function startedAheadOf(msId) { const ix = index(); return (ix && ix.aheadByMs[msId]) || [] }

/** True when a leader let `msId` begin before `predId` posted. */
export function isStartedAhead(msId, predId) { return startedAheadOf(msId).some(r => r.predecessor_airtable_id === predId) }

/** The post record of a milestone, or null. */
export function postRecordOf(msId) { const ix = index(); return (ix && ix.postByMs[msId]) || null }

if (typeof window !== 'undefined') {
  window.addEventListener('milestone-flow-changed', () => { ctx.milestoneFlow = undefined; ensureMilestoneFlow() })
}
